import {
  GoalHierarchy_ObjectiveWithParentsAndChildrenFragment,
  RelatedObjectiveFragment,
} from '../../../../../__generated__/graphql';
import { mapRelatedGoal } from '../../mappings';
import { GoalHierarchyItem, GoalHierarchyItemToRender } from './types';

export const mapGoalHierarchy = (
  goal: GoalHierarchy_ObjectiveWithParentsAndChildrenFragment,
): GoalHierarchyItem => {
  const { rootGoal, currentGoal } = buildParentsHierarchy(
    goal,
    goal.allParents,
  );

  currentGoal.children.push(
    ...buildChildrenHierarchy(goal.Id, goal.allChildren),
  );

  return rootGoal;
};

// flatten the hierarchy to a flat array of GoalHierarchyItemToRender
export const mapGoalHierarchyToFlatArray = (
  goal: GoalHierarchyItem,
): GoalHierarchyItemToRender[] => {
  const result: GoalHierarchyItemToRender[] = [];
  const stack: Array<{ item: GoalHierarchyItem; level: number }> = [
    { item: goal, level: 0 },
  ];

  while (stack.length > 0) {
    const { item, level } = stack.pop()!;
    const { children, ...itemWithoutChildren } = item;
    result.push({
      ...itemWithoutChildren,
      level,
    });

    for (let i = children.length - 1; i >= 0; i--) {
      stack.push({ item: children[i], level: level + 1 });
    }
  }

  return result;
};

const buildParentsHierarchy = (
  goal: RelatedObjectiveFragment,
  allParents: GoalHierarchy_ObjectiveWithParentsAndChildrenFragment['allParents'],
): { rootGoal: GoalHierarchyItem; currentGoal: GoalHierarchyItem } => {
  const rootGoalFragment: RelatedObjectiveFragment =
    allParents.length > 0 ? allParents[allParents.length - 1] : goal;

  const rootGoal: GoalHierarchyItem = {
    ...mapRelatedGoal(rootGoalFragment),
    children: [],
  };

  let currentGoal: GoalHierarchyItem = rootGoal;
  for (let i = allParents.length - 1; i >= 0; i--) {
    const curChild = i > 0 ? allParents[i - 1] : goal;
    currentGoal.children.push({
      ...mapRelatedGoal(curChild),
      children: [],
    });
    currentGoal = currentGoal.children[currentGoal.children.length - 1];
  }

  return { rootGoal, currentGoal };
};

const buildChildrenHierarchy = (
  parentId: string,
  allChildren: GoalHierarchy_ObjectiveWithParentsAndChildrenFragment['allChildren'],
): GoalHierarchyItem[] => {
  const directChildren = allChildren.filter(
    (child) => child.parent?.Id === parentId,
  );

  return directChildren.map((child) => ({
    ...mapRelatedGoal(child),
    children: buildChildrenHierarchy(child.Id, allChildren),
  }));
};
